import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CircleDot, ListChecks, ToggleLeft, TextCursorInput, Hash, Type, AlignLeft, Code2, Mic, Sparkles, CircleCheck, CircleAlert } from 'lucide-react';
import { useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';

/**
 * /assess/bank/questions/new — direct port of kollez/pages/assess/question.html.
 * Wired to POST /api/v1/assessments/questions. Requires an existing QuestionBank
 * in the tenant; falls back to a helper hint when none exists.
 */
type QType = 'single' | 'multi' | 'tf' | 'blank' | 'num' | 'short' | 'long' | 'code' | 'voice';

const TYPES: Array<[QType, string, typeof CircleDot]> = [
  ['single', 'Single choice', CircleDot],
  ['multi', 'Multiple choice', ListChecks],
  ['tf', 'True / false', ToggleLeft],
  ['blank', 'Fill in blanks', TextCursorInput],
  ['num', 'Numeric', Hash],
  ['short', 'Short answer', Type],
  ['long', 'Long answer', AlignLeft],
  ['code', 'Code', Code2],
  ['voice', 'Spoken answer', Mic],
];

type Bank = { _id: string; name: string; questionCount?: number };

export function AssessQuestionEditorPage() {
  const toast = useToast();
  const nav = useNavigate();

  const [banks, setBanks] = useState<Bank[]>([]);
  const [bankId, setBankId] = useState('');
  const [type, setType] = useState<QType>('single');
  const [stem, setStem] = useState('Which SQL clause filters rows after grouping?');
  const [options, setOptions] = useState(['WHERE', 'HAVING', 'ORDER BY', 'LIMIT']);
  const [correct, setCorrect] = useState<number[]>([1]);
  const [expectedText, setExpectedText] = useState('');
  const [rubric, setRubric] = useState('');
  const [testCases, setTestCases] = useState('');
  const [voicePrompt, setVoicePrompt] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [marks, setMarks] = useState(2);
  const [timeGuideSec, setTimeGuideSec] = useState(60);
  const [skill, setSkill] = useState('SQL');
  const [wrongPenalty, setWrongPenalty] = useState(0);
  const [mustPass, setMustPass] = useState(false);
  const [explanation, setExplanation] = useState('HAVING filters groups after GROUP BY; WHERE filters rows before grouping.');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet<{ banks: Bank[] }>('/assessments/banks?limit=50')
      .then((r) => {
        setBanks(r.banks ?? []);
        if (r.banks?.[0]) setBankId(r.banks[0]._id);
      })
      .catch(() => {});
  }, []);

  function toggleCorrect(i: number) {
    if (type === 'multi') {
      setCorrect((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]));
    } else {
      setCorrect([i]);
    }
  }

  function correctAnswerPayload() {
    if (type === 'single' || type === 'multi') return correct;
    if (type === 'tf') return correct[0] === 0;
    if (type === 'num') return Number(expectedText);
    if (type === 'blank' || type === 'short') return expectedText;
    if (type === 'code') return testCases;
    return undefined;
  }

  async function submit(status: 'draft' | 'review') {
    if (!bankId) {
      toast.show({ tone: 'warning', message: 'Pick a question bank first. Create one in /assess/bank.' });
      return;
    }
    if (!stem.trim() || stem.trim().length < 3) {
      toast.show({ tone: 'warning', message: 'Write the question stem (min 3 characters).' });
      return;
    }
    if ((type === 'single' || type === 'multi') && correct.length === 0) {
      toast.show({ tone: 'warning', message: 'Mark at least one option as correct.' });
      return;
    }

    setSaving(true);
    try {
      const body = {
        questionBank: bankId,
        stem,
        type,
        options: (type === 'single' || type === 'multi')
          ? options.map((text, i) => ({ text, correct: correct.includes(i) }))
          : undefined,
        correctAnswer: correctAnswerPayload(),
        rubric: type === 'long' ? rubric : undefined,
        explanation: explanation || undefined,
        skill: skill || undefined,
        difficulty,
        marks,
        timeGuideSec,
        wrongPenalty,
        mustPass,
      };
      await apiPost<{ question: unknown }>('/assessments/questions', body);
      toast.show({
        tone: 'success',
        message:
          status === 'review'
            ? 'Sent for review. A second person approves before this question can appear in a live exam.'
            : 'Saved as draft. Practice sets can use it straight away.',
      });
      nav('/assess/bank');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save question';
      toast.show({ tone: 'warning', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .qe { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1180px) { .qe { grid-template-columns: minmax(0,1fr) 380px; } .qe-side { position: sticky; top: 76px; } }
        .qtypes { display: grid; grid-template-columns: repeat(auto-fill, minmax(132px, 1fr)); gap: 6px; }
        .qtype-btn { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid rgb(var(--line-secondary)); border-radius: 8px; background: rgb(var(--bg-primary)); font-size: .8125rem; color: rgb(var(--ink-secondary)); cursor: pointer; text-align: left; }
        .qtype-btn[aria-pressed="true"] { border-color: rgb(var(--ink-accent)); color: rgb(var(--ink-accent)); background: rgb(var(--bg-info)); }
        .pv-bubble { background: rgb(var(--bg-primary)); border: 1px solid rgb(var(--line-tertiary)); border-radius: 4px 14px 14px 14px; padding: 14px 16px; display: grid; gap: 10px; }
        .pv-head { display: flex; align-items: center; gap: 8px; font-size: .75rem; color: rgb(var(--ink-tertiary)); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Question bank · new question</p>
          <h1 className="h-page">New question</h1>
          <p className="subhead">Choose a type, write it once, and check the preview candidates will see in the chat.</p>
        </div>
        <div className="ph-actions">
          <span className="helper">{saving ? 'Saving…' : 'Not saved'}</span>
        </div>
      </div>

      <div className="qe">
        <div className="grid gap-5 min-w-0">
          <section className="card card-pad grid gap-3">
            <h2 className="h-section">Question bank</h2>
            {banks.length === 0 ? (
              <p className="helper">No question banks yet. Create one on <Link to="/assess/bank" style={{ color: 'rgb(var(--ink-accent))' }}>/assess/bank</Link> first.</p>
            ) : (
              <select className="select" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                {banks.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}{b.questionCount ? ` · ${b.questionCount} q` : ''}</option>
                ))}
              </select>
            )}
          </section>

          <section className="card card-pad grid gap-3">
            <h2 className="h-section">Type</h2>
            <div className="qtypes" role="group">
              {TYPES.map(([k, l, Icon]) => (
                <button key={k} className="qtype-btn" aria-pressed={type === k} onClick={() => setType(k)}>
                  <Icon size={14} />{l}
                </button>
              ))}
            </div>
          </section>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section">Question</h2>
            <label className="grid gap-1.5">
              <span className="label">Question text</span>
              <textarea className="textarea" rows={3} value={stem} onChange={(e) => setStem(e.target.value)} />
            </label>

            {(type === 'single' || type === 'multi') && (
              <div className="grid gap-2">
                <span className="label">Options</span>
                {options.map((o, i) => (
                  <div key={i} className="grid items-center gap-2" style={{ gridTemplateColumns: '28px minmax(0,1fr) 36px' }}>
                    <input
                      type={type === 'multi' ? 'checkbox' : 'radio'}
                      className="check"
                      checked={correct.includes(i)}
                      onChange={() => toggleCorrect(i)}
                      aria-label={`Mark option ${i + 1} correct`}
                    />
                    <input className="input" value={o} onChange={(e) => setOptions((os) => os.map((x, j) => (j === i ? e.target.value : x)))} />
                    <button className="btn btn-ghost btn-icon btn-sm" aria-label="Remove" onClick={() => setOptions((os) => os.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <button className="btn btn-outline btn-sm justify-self-start" onClick={() => setOptions((os) => [...os, ''])}>Add option</button>
              </div>
            )}

            {type === 'tf' && (
              <div className="grid gap-2">
                <span className="label">Correct answer</span>
                <div className="segmented">
                  <button aria-pressed={correct[0] === 0} onClick={() => setCorrect([0])}>True</button>
                  <button aria-pressed={correct[0] === 1} onClick={() => setCorrect([1])}>False</button>
                </div>
              </div>
            )}

            {(type === 'blank' || type === 'num' || type === 'short') && (
              <label className="grid gap-1.5">
                <span className="label">Expected answer</span>
                <input
                  className="input"
                  value={expectedText}
                  onChange={(e) => setExpectedText(e.target.value)}
                  placeholder={type === 'num' ? 'e.g. 36' : type === 'blank' ? 'e.g. HAVING' : 'Key points'}
                />
              </label>
            )}

            {type === 'long' && (
              <label className="grid gap-1.5">
                <span className="label">Rubric (Nexie grades against this)</span>
                <textarea className="textarea" rows={4} value={rubric} onChange={(e) => setRubric(e.target.value)} placeholder="Point 1 (1 mark) · …" />
              </label>
            )}

            {type === 'code' && (
              <label className="grid gap-1.5">
                <span className="label">Test cases</span>
                <textarea
                  className="textarea"
                  rows={4}
                  value={testCases}
                  onChange={(e) => setTestCases(e.target.value)}
                  placeholder="input → expected output"
                  style={{ fontFamily: 'monospace' }}
                />
              </label>
            )}

            {type === 'voice' && (
              <label className="grid gap-1.5">
                <span className="label">Prompt for the candidate to answer aloud</span>
                <textarea
                  className="textarea"
                  rows={2}
                  value={voicePrompt}
                  onChange={(e) => setVoicePrompt(e.target.value)}
                  placeholder='e.g. "Tell me about a project you built."'
                />
              </label>
            )}
          </section>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section">Level, marks and tags</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <span className="label">Level</span>
                <div className="segmented">
                  {(['easy', 'medium', 'hard'] as const).map((d) => (
                    <button key={d} aria-pressed={difficulty === d} onClick={() => setDifficulty(d)}>{d.charAt(0).toUpperCase() + d.slice(1)}</button>
                  ))}
                </div>
              </div>
              <label className="grid gap-1.5"><span className="label">Marks</span><input className="input" type="number" value={marks} min={0} onChange={(e) => setMarks(+e.target.value)} /></label>
              <label className="grid gap-1.5"><span className="label">Time guide</span><input className="input" type="number" value={timeGuideSec} min={0} onChange={(e) => setTimeGuideSec(+e.target.value)} /><span className="helper">Seconds · used to spot rushed answers</span></label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5"><span className="label">Skill</span><input className="input" value={skill} onChange={(e) => setSkill(e.target.value)} /></label>
              <label className="grid gap-1.5">
                <span className="label">Wrong answer</span>
                <select className="select" value={wrongPenalty} onChange={(e) => setWrongPenalty(+e.target.value)}>
                  <option value={0}>No penalty</option>
                  <option value={0.25}>Minus a quarter of the marks</option>
                  <option value={0.5}>Minus half the marks</option>
                </select>
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="check mt-0.5" checked={mustPass} onChange={(e) => setMustPass(e.target.checked)} />
              <span>Must-pass question <span className="helper block">Getting this wrong fails the exam regardless of total score. Use rarely.</span></span>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Explanation <span className="cell-sub">(shown in practice mode and to reviewers)</span></span>
              <textarea className="textarea" rows={2} value={explanation} onChange={(e) => setExplanation(e.target.value)} />
            </label>
          </section>
        </div>

        <aside className="qe-side grid gap-4">
          <div className="card card-pad grid gap-3" style={{ background: 'rgb(var(--bg-secondary))' }}>
            <p className="eyebrow m-0">Candidate sees</p>
            <div className="pv-bubble">
              <div className="pv-head"><Sparkles size={12} />Nexie · Question 4 of 30 · {marks} marks</div>
              <div style={{ fontSize: 15 }}>{stem || '(question text)'}</div>
              <div className="grid gap-2">
                {(type === 'single' || type === 'multi') && options.map((o, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm">
                    <input type={type === 'multi' ? 'checkbox' : 'radio'} className="check" disabled /> {o || `Option ${i + 1}`}
                  </label>
                ))}
                {type === 'tf' && <div className="segmented"><button>True</button><button>False</button></div>}
              </div>
            </div>
          </div>

          <div className="card card-pad grid gap-2">
            <div className="flex items-center justify-between">
              <h2 className="h-section">Checks</h2>
              <button className="btn btn-ghost btn-sm" type="button"><Sparkles size={12} />Ask Nexie to review</button>
            </div>
            <div className="qcheck ok"><CircleCheck size={14} /><span>Stem is clear ({stem.trim().split(/\s+/).length} words)</span></div>
            <div className={correct.length > 0 || type !== 'single' && type !== 'multi' ? 'qcheck ok' : 'qcheck warn'}>
              {(correct.length > 0 || (type !== 'single' && type !== 'multi')) ? <CircleCheck size={14} /> : <CircleAlert size={14} />}
              <span>{(correct.length > 0 || (type !== 'single' && type !== 'multi')) ? 'At least one option marked correct' : 'Mark the correct option(s)'}</span>
            </div>
            <div className={bankId ? 'qcheck ok' : 'qcheck warn'}>
              {bankId ? <CircleCheck size={14} /> : <CircleAlert size={14} />}
              <span>{bankId ? 'Question bank picked' : 'Pick a bank'}</span>
            </div>
            <div className="qcheck ok"><CircleCheck size={14} /><span>Difficulty tagged</span></div>
          </div>

          <div className="card card-pad grid gap-2">
            <p className="helper m-0" style={{ color: 'rgb(var(--ink-tertiary))', fontSize: 12 }}>
              Review for gender / regional / caste neutrality before approving. AI-generated
              content still needs a human's eye.
            </p>
            <button className="btn btn-primary" type="button" disabled={saving} onClick={() => submit('review')}>
              {saving ? 'Sending…' : 'Send for review'}
            </button>
            <button className="btn btn-outline" type="button" disabled={saving} onClick={() => submit('draft')}>
              Save as draft
            </button>
            <p className="helper m-0 text-center">Practice sets can use it straight away.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
